import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGameStore } from '../stores/gameStore'
import { useSocket } from '../context/SocketContext'
import LobbyScreen from '../components/LobbyScreen'
import MatchRequestScreen from '../components/MatchRequestScreen'

const LobbyPage = () => {
  const navigate = useNavigate()
  
  const {
    player,
    gameState,
    lobbyPlayers,
    matchRequestData,
    error,
    clearError,
    setExplicitlyLeftLobby,
  } = useGameStore()
  
  const { joinLobby, requestMatch, respondToMatch, leaveLobby } = useSocket()

  useEffect(() => {
    // Join lobby when component mounts
    if (player.identity) {
      joinLobby()
    } else {
      navigate('/')
    }
  }, [player.identity, navigate])

  // Scroll to top when entering lobby
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    // Handle game state changes - just navigate to game when it starts
    if (gameState === 'in_game') {
      navigate('/game')
    }
  }, [gameState, navigate])

  const handleMatchUser = (targetUserId) => {
    clearError()
    requestMatch(targetUserId)
  }

  const handleAcceptMatch = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToMatch(matchRequestData.requester_id, true)
    }
  }

  const handleDeclineMatch = () => {
    if (matchRequestData && matchRequestData.requester_id) {
      respondToMatch(matchRequestData.requester_id, false)
    }
  }

  const handleLeaveLobby = () => {
    leaveLobby()
    setExplicitlyLeftLobby(true)
    navigate('/')
  }

  // Download recovery key functionality
  const handleDownloadRecoveryKey = () => {
    const content = `COMMON GROUND - Recovery Key Backup
=====================================

Your Recovery Key: ${player.recoveryKey}
Identity: ${player.identity}
Souls Helped: ${player.soulsHelped}
Generated: ${new Date().toLocaleString()}

IMPORTANT INSTRUCTIONS:
• Keep this recovery key safe and secure
• You need this word-based key to recover your identity
• Never share this key with anyone
• If you lose this key, you cannot recover your account

How to use:
1. Go to the Common Ground homepage
2. Click "Recover Existing Identity"
3. Enter your recovery key: ${player.recoveryKey}
4. Click "Recover Identity"

Your journey through purgatory continues...`

    // Create and download the file
    const blob = new Blob([content], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `common-ground-recovery-${player.identity?.replace(/[^\w]/g, '')}-${player.recoveryKey}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  // Fallback clipboard function for older browsers
  const fallbackCopyToClipboard = (text) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      
      // Try to copy using execCommand
      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)
      
      if (successful) {
        showShareFeedback('Copied to clipboard! 📋')
      } else {
        throw new Error('execCommand failed')
      }
    } catch (error) {
      console.error('Fallback clipboard error:', error)
      showShareFeedback('Share manually: ' + text.substring(0, 50) + '...')
    }
  }

  // Share functionality
  const handleShare = async () => {
    // Get metadata from HTML head
    const getMetaContent = (property) => {
      const meta = document.querySelector(`meta[property="${property}"], meta[name="${property}"]`)
      return meta ? meta.getAttribute('content') : null
    }
    
    const ogTitle = getMetaContent('og:title') || document.title
    const ogDescription = getMetaContent('og:description') || getMetaContent('description')
    const ogImage = getMetaContent('og:image')
    const ogUrl = getMetaContent('og:url') || window.location.origin
    
    const shareData = {
      title: ogTitle || "Join me in Common Ground!",
      text: `I'm waiting to escape purgatory in Common Ground! I'm ${player.identity} and I've helped ${player.soulsHelped || 0} souls escape. Come play with me and lets see if we can find common ground.`,
      url: ogUrl || window.location.origin
    }
    
    // Add Open Graph data if available
    if (ogImage) {
      shareData.image = ogImage
    }

    // Add hashtags
    const shareText = `${shareData.text}\n\n#CommonGround #PurgatoryEscape #WildGriffin\n\n${shareData.url}`

    try {
      // Try native sharing first (mobile)
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData)
        showShareFeedback('Shared successfully! 🎉')
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        // Modern clipboard API
        await navigator.clipboard.writeText(shareText)
        showShareFeedback('Copied to clipboard! 📋')
      } else {
        // Fallback for older browsers
        fallbackCopyToClipboard(shareText)
      }
    } catch (error) {
      console.error('Error sharing:', error)
      // Final fallback - try legacy clipboard method
      fallbackCopyToClipboard(shareText)
    }
  }

  // Show share feedback
  const showShareFeedback = (message) => {
    const feedback = document.createElement('div')
    feedback.textContent = message
    feedback.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: #1a1a1a;
      padding: 12px 20px;
      border-radius: 8px;
      font-weight: bold;
      z-index: 1000;
      animation: slideInRight 0.3s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `
    
    // Add animation styles if not already present
    if (!document.querySelector('#share-feedback-styles')) {
      const style = document.createElement('style')
      style.id = 'share-feedback-styles'
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `
      document.head.appendChild(style)
    }
    
    document.body.appendChild(feedback)
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      if (feedback.parentNode) {
        feedback.style.animation = 'slideOutRight 0.3s ease'
        setTimeout(() => {
          if (feedback.parentNode) {
            feedback.remove()
          }
        }, 300)
      }
    }, 3000)
  }

  // Show match request states
  if (gameState === 'match_request_sent' || gameState === 'match_request_received') {
    return (
      <MatchRequestScreen
        gameState={gameState}
        matchRequestData={matchRequestData}
        onAccept={handleAcceptMatch}
        onDecline={handleDeclineMatch}
        onCancel={handleLeaveLobby}
      />
    )
  }

  // Show normal lobby
  return (
    <LobbyScreen
      player={player}
      lobbyPlayers={lobbyPlayers}
      onMatchUser={handleMatchUser}
      onLeaveLobby={handleLeaveLobby}
      onShareWithFriends={handleShare}
      onDownloadKey={handleDownloadRecoveryKey}
      onGoToAnalytics={() => navigate('/analytics')}
      onGoToAbout={() => navigate('/about')}
    />
  )
}

export default LobbyPage
